/**
 * Dona de participación por categoría (lecturas).
 *
 * Cada sector es un `<circle>` con `pathLength={100}`, de modo que el
 * `stroke-dasharray` se expresa directamente en porcentaje. Sin dependencias y
 * con leyenda al lado para no depender del color.
 */

import { formatNumber } from '@/lib/utils';

export interface SectorDona {
  etiqueta: string;
  valor: number;
}

const COLORES = ['#06b6d4', '#10b981', '#f59e0b', '#0ea5e9', '#8b5cf6', '#f43f5e'];

export function GraficoDona({ datos }: { datos: SectorDona[] }) {
  const total = datos.reduce((suma, sector) => suma + sector.valor, 0);

  if (!total) {
    return <p className="text-sm text-ink-400">Sin lecturas en este periodo.</p>;
  }

  const visibles = datos.filter((sector) => sector.valor > 0);

  // El desplazamiento de cada sector es la suma de los anteriores (hay pocas
  // categorías, así que no compensa acumular con una variable mutable).
  const sectores = visibles.map((sector, indice) => {
    const porcentaje = (sector.valor / total) * 100;
    const anteriores = visibles
      .slice(0, indice)
      .reduce((suma, previo) => suma + previo.valor, 0);

    return {
      ...sector,
      porcentaje,
      desplazamiento: (anteriores / total) * 100,
      color: COLORES[indice % COLORES.length],
    };
  });

  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row">
      <div className="relative shrink-0">
        <svg
          viewBox="0 0 120 120"
          className="size-36 -rotate-90"
          role="img"
          aria-label="Participación de lecturas por categoría"
        >
          <circle cx="60" cy="60" r="46" fill="none" stroke="#f1f5f9" strokeWidth="14" />

          {sectores.map((sector) => (
            <circle
              key={sector.etiqueta}
              cx="60"
              cy="60"
              r="46"
              fill="none"
              stroke={sector.color}
              strokeWidth="14"
              pathLength={100}
              strokeDasharray={`${sector.porcentaje} ${100 - sector.porcentaje}`}
              strokeDashoffset={-sector.desplazamiento}
            >
              <title>{`${sector.etiqueta}: ${formatNumber(sector.valor)} lecturas (${sector.porcentaje.toFixed(1)}%)`}</title>
            </circle>
          ))}
        </svg>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold tabular-nums text-ink-900">
            {formatNumber(total)}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">
            lecturas
          </span>
        </div>
      </div>

      <ul className="min-w-0 flex-1 space-y-2">
        {sectores.map((sector) => (
          <li key={sector.etiqueta} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-2">
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: sector.color }}
                aria-hidden="true"
              />
              <span className="truncate text-ink-700" title={sector.etiqueta}>
                {sector.etiqueta}
              </span>
            </span>
            <span className="shrink-0 tabular-nums text-ink-500">
              {sector.porcentaje.toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
